import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { Download, TrendingUp, TrendingDown, Wallet, Calendar, FileText, User, Building2, DollarSign, Eye, RefreshCw, ArrowRightLeft, ChevronDown } from "lucide-react";

// Función para obtener colores de vista
const getVistaColor = () => {
  return {
    gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    solid: "#06b6d4",
    light: "#06b6d410",
    description: "Flujo de caja del proyecto",
  };
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

// Función para formatear monto con puntos cada 3 números, sin decimales innecesarios
const formatearMonto = (valor) => {
  if (!valor) return "";
  const numero = parseFloat(valor);
  return numero.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Dropdown personalizado (copiado de Salidas)
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    console.log('🎨 CustomDropdown - Actualizando opción seleccionada:', {
      value,
      selected,
      options: options.map(opt => ({ value: opt.value, label: opt.label })),
      timestamp: new Date().toISOString()
    });
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "white",
            border: "2px solid #f1f5f9",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            marginTop: "4px",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#f0f9ff" : "transparent",
                color: option.value === value ? "#0369a1" : "#1e293b",
                fontSize: "14px",
                fontWeight: option.value === value ? "600" : "500",
                textAlign: "left",
                cursor: "pointer",
                borderBottom: index < options.length - 1 ? "1px solid #f1f5f9" : "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.15s ease",
                touchAction: "manipulation",
              }}
              onMouseOver={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseOut={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function CajaCliente({ constructora, usuario, permisosCliente, location }) {
  const [loading, setLoading] = useState(true);
  const [transacciones, setTransacciones] = useState([]);
  const [saldosCajas, setSaldosCajas] = useState({ ARS: 0, USD: 0 });
  const [proyectoCliente, setProyectoCliente] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  
  const isCliente = usuario?.tipo_usuario === "cliente";
  const isConstructora = usuario?.tipo_usuario === "constructora" || usuario?.tipo_usuario === "empleado";

  // Manejar estado de navegación desde Dashboard
  useEffect(() => {
    if (location?.state) {
      const { proyectoSeleccionado: proyectoDesdeDashboard, monedaFiltro } = location.state;
      
      console.log('🔍 Navegación desde Dashboard:', {
        proyectoDesdeDashboard,
        monedaFiltro,
        isConstructora,
        locationState: location.state
      });
      
      if (proyectoDesdeDashboard && isConstructora && proyectos.length > 0) {
        // Si viene un proyecto desde el Dashboard, seleccionarlo
        console.log('📍 Estableciendo proyecto desde Dashboard:', proyectoDesdeDashboard);
        
        // Verificar si el proyecto existe en la lista de proyectos disponibles
        const proyectoExiste = proyectos.find(p => p.id === proyectoDesdeDashboard);
        console.log('🔍 Verificando si proyecto existe en lista:', {
          proyectoDesdeDashboard,
          proyectosDisponibles: proyectos.map(p => ({ id: p.id, codigo: p.codigo })),
          proyectoExiste
        });
        
        if (proyectoExiste) {
          console.log('✅ Proyecto encontrado, estableciendo como seleccionado');
          setProyectoSeleccionado(proyectoDesdeDashboard);
          
          // Cargar datos del proyecto específico inmediatamente
          console.log('🚀 Cargando datos del proyecto desde Dashboard:', proyectoDesdeDashboard);
          cargarDatosConstructora(proyectoDesdeDashboard);
          
          // Limpiar el estado de navegación para evitar que se ejecute múltiples veces
          window.history.replaceState({}, document.title);
        } else {
          console.log('❌ Proyecto no encontrado en la lista, esperando a que se carguen los proyectos');
        }
      } else if (proyectoDesdeDashboard && isConstructora && proyectos.length === 0) {
        console.log('⏳ Proyectos aún no cargados, esperando...');
      }
      
      // TODO: Implementar filtro por moneda si es necesario
      console.log('Moneda filtro desde Dashboard:', monedaFiltro);
    }
  }, [location?.state, isConstructora, proyectos]);

  const colorVista = getVistaColor();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isCliente && constructora?.id && permisosCliente?.proyectoId) {
      cargarDatos();
    } else if (isConstructora && constructora?.id) {
      cargarProyectosConstructora();
    }
  }, [constructora?.id, permisosCliente?.proyectoId, isCliente, isConstructora]);

  useEffect(() => {
    console.log('🔄 useEffect proyectoSeleccionado ejecutado:', {
      isConstructora,
      proyectoSeleccionado,
      timestamp: new Date().toISOString()
    });
    
    // No ejecutar si hay estado de navegación pendiente (para evitar conflictos con navegación desde Dashboard)
    if (location?.state?.proyectoSeleccionado) {
      console.log('⏸️ Saltando useEffect por navegación desde Dashboard pendiente');
      return;
    }
    
    if (isConstructora && proyectoSeleccionado) {
      console.log('📋 Cargando datos de proyecto específico:', proyectoSeleccionado);
      cargarDatosConstructora(proyectoSeleccionado);
    }
  }, [proyectoSeleccionado, isConstructora, location?.state]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      await Promise.all([
        cargarProyectoCliente(),
        cargarSaldosCajas(),
        cargarTransacciones(),
      ]);
    } catch (error) {
      console.error("Error cargando datos de caja:", error);
    } finally {
      setLoading(false);
    }
  };

  const cargarTodasLasTransacciones = async () => {
    setLoading(true);
    try {
      // Cargar TODAS las cajas de la constructora (de todos los proyectos)
      const { data: todasLasCajas } = await supabase
        .from("cajas")
        .select("id, nombre, moneda, proyecto_id, balance_actual")
        .eq("constructora_id", constructora.id)
        .eq("activo", true);

      const todasLasCajasIds = (todasLasCajas || []).map(c => c.id);
      const cajasMap = {};
      todasLasCajas?.forEach(caja => {
        cajasMap[caja.id] = { 
          nombre: caja.nombre, 
          moneda: caja.moneda,
          proyecto_id: caja.proyecto_id 
        };
      });

      // Cargar proyectos para mostrar nombres
      const { data: todosLosProyectos } = await supabase
        .from("proyectos")
        .select("id, codigo")
        .eq("constructora_id", constructora.id)
        .eq("activo", true);

      const proyectosMap = {};
      todosLosProyectos?.forEach(proyecto => {
        proyectosMap[proyecto.id] = proyecto.codigo;
      });

      // Cargar conceptos
      const { data: conceptosEntrada } = await supabase
        .from("conceptos_entrada")
        .select("id, nombre")
        .eq("constructora_id", constructora.id);

      const { data: conceptosSalida } = await supabase
        .from("conceptos_salida")
        .select("id, nombre")
        .eq("constructora_id", constructora.id);

      const conceptosMap = {};
      conceptosEntrada?.forEach(c => { conceptosMap[c.id] = c.nombre; });
      conceptosSalida?.forEach(c => { conceptosMap[c.id] = c.nombre; });

      // Cargar TODAS las entradas y filtrar solo "Adelanto de caja"
      const { data: entradas } = await supabase
        .from("entradas")
        .select("id, fecha, created_at, monto, moneda, tipo_cambio, descripcion, comprobante_url, factura_url, tiene_comprobante, tiene_factura, concepto_id, caja_destino_id, caja_origen_id, proyecto_id")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null);

      // Mantener solo entradas con concepto exactamente "Adelanto de caja" (o alias "Adelanto caja")
      const entradasAdelantoCaja = (entradas || []).filter(e => {
        const nombre = (conceptosMap[e.concepto_id] || "").toLowerCase();
        return nombre === "adelanto de caja" || nombre === "adelanto caja";
      });

      // Cargar SOLO las salidas que son desde cajas de proyecto
      const { data: salidas } = await supabase
        .from("salidas")
        .select("id, fecha, created_at, monto, moneda, tipo_cambio, descripcion, comprobante_url, factura_url, tiene_comprobante, tiene_factura, concepto_id, caja_origen_id, proyecto_id")
        .eq("constructora_id", constructora.id)
        .in("caja_origen_id", todasLasCajasIds)
        .eq("activo", true)
        .is("deleted_at", null);

      // Cargar cambios de moneda de TODAS las cajas
      const { data: cambios } = await supabase
        .from("cambios_moneda")
        .select("id, fecha, created_at, monto_origen, monto_destino, moneda_origen, moneda_destino, tipo_cambio, descripcion, caja_origen_id, caja_destino_id, proyecto_id")
        .eq("constructora_id", constructora.id)
        .or(`caja_origen_id.in.(${todasLasCajasIds.join(",")}),caja_destino_id.in.(${todasLasCajasIds.join(",")})`)
        .eq("activo", true)
        .is("deleted_at", null);

      // Cargar información de todas las cajas (incluyendo las que no son de proyecto)
      const { data: todasLasCajasCompletas } = await supabase
        .from("cajas")
        .select("id, nombre, moneda, tipo_caja, proyecto_id, balance_actual")
        .eq("constructora_id", constructora.id)
        .eq("activo", true);

      const todasLasCajasCompletasMap = {};
      todasLasCajasCompletas?.forEach(caja => {
        todasLasCajasCompletasMap[caja.id] = { 
          nombre: caja.nombre, 
          moneda: caja.moneda,
          tipo_caja: caja.tipo_caja,
          proyecto_id: caja.proyecto_id 
        };
      });

      const todasTransacciones = [
        ...(entradasAdelantoCaja || []).map(entrada => ({
          ...entrada,
          tipo: "entrada",
          concepto_nombre: conceptosMap[entrada.concepto_id] || "Entrada",
          caja_nombre: todasLasCajasCompletasMap[entrada.caja_destino_id]?.proyecto_id 
            ? `${todasLasCajasCompletasMap[entrada.caja_destino_id]?.nombre || "Caja"} (${proyectosMap[todasLasCajasCompletasMap[entrada.caja_destino_id]?.proyecto_id] || "Proyecto"})`
            : todasLasCajasCompletasMap[entrada.caja_destino_id]?.nombre || "Caja",
          caja_moneda: todasLasCajasCompletasMap[entrada.caja_destino_id]?.moneda || "ARS"
        })),
        ...(salidas || []).map(salida => ({
          ...salida,
          tipo: "salida",
          concepto_nombre: conceptosMap[salida.concepto_id] || "Gasto",
          caja_nombre: `${cajasMap[salida.caja_origen_id]?.nombre || "Caja"} (${proyectosMap[salida.proyecto_id] || "Proyecto"})`,
          caja_moneda: cajasMap[salida.caja_origen_id]?.moneda || "ARS"
        })),
        ...(cambios || []).flatMap(cambio => {
          const baseTime = new Date(cambio.created_at).getTime();
          const salidaTime = new Date(baseTime - 1000);
          const entradaTime = new Date(baseTime);
          
          return [
            {
              id: `${cambio.id}_salida`,
              fecha: cambio.fecha,
              created_at: salidaTime.toISOString(),
              tipo: "cambio_salida",
              monto: cambio.monto_origen,
              moneda: cambio.moneda_origen,
              tipo_cambio: cambio.tipo_cambio,
              descripcion: `Cambio USD a ARS - ${cambio.descripcion || ""}`,
              comprobante_url: null,
              factura_url: null,
              tiene_comprobante: false,
              tiene_factura: false,
              concepto_id: null,
              caja_origen_id: cambio.caja_origen_id,
              caja_destino_id: null,
              concepto_nombre: "Cambio de moneda",
              caja_nombre: `${cajasMap[cambio.caja_origen_id]?.nombre || "Caja"} (${proyectosMap[cambio.proyecto_id] || "Proyecto"})`,
              caja_moneda: cambio.moneda_origen
            },
            {
              id: `${cambio.id}_entrada`,
              fecha: cambio.fecha,
              created_at: entradaTime.toISOString(),
              tipo: "cambio_entrada",
              monto: cambio.monto_destino,
              moneda: cambio.moneda_destino,
              tipo_cambio: cambio.tipo_cambio,
              descripcion: `Cambio USD a ARS - ${cambio.descripcion || ""}`,
              comprobante_url: null,
              factura_url: null,
              tiene_comprobante: false,
              tiene_factura: false,
              concepto_id: null,
              caja_origen_id: null,
              caja_destino_id: cambio.caja_destino_id,
              concepto_nombre: "Cambio de moneda",
              caja_nombre: `${cajasMap[cambio.caja_destino_id]?.nombre || "Caja"} (${proyectosMap[cambio.proyecto_id] || "Proyecto"})`,
              caja_moneda: cambio.moneda_destino
            }
          ];
        })
      ];

      // Calcular saldos por moneda en base al flujo filtrado
      const { transaccionesARSConSaldo, transaccionesUSDConSaldo } = calcularSaldosPorMoneda(todasTransacciones);
      
      // Combinar y ordenar: primero por FECHA, luego por CREATED_AT (MÁS RECIENTE PRIMERO)
      const transaccionesConSaldo = [...transaccionesARSConSaldo, ...transaccionesUSDConSaldo].sort((a, b) => {
        const fechaA = new Date(a.fecha).getTime();
        const fechaB = new Date(b.fecha).getTime();
        
        // Si las fechas son diferentes, ordenar por fecha
        if (fechaA !== fechaB) {
          return fechaB - fechaA; // Descendente: más reciente primero
        }
        
        // Si las fechas son iguales, desempatar por created_at
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return createdB - createdA; // Descendente: más reciente primero
      });

      // Saldos mostrados = saldo acumulado más reciente por moneda (último calculado)
      const saldoARS = transaccionesARSConSaldo.length > 0 
        ? transaccionesARSConSaldo[transaccionesARSConSaldo.length - 1].saldoAcumulado 
        : 0;
      const saldoUSD = transaccionesUSDConSaldo.length > 0 
        ? transaccionesUSDConSaldo[transaccionesUSDConSaldo.length - 1].saldoAcumulado 
        : 0;
      setSaldosCajas({ ARS: saldoARS, USD: saldoUSD });

      setTransacciones(transaccionesConSaldo);
    } catch (error) {
      console.error("Error cargando todas las transacciones:", error);
    } finally {
      setLoading(false);
    }
  };

  const cargarProyectosConstructora = async () => {
    try {
      const { data, error } = await supabase
        .from("proyectos")
        .select("id, codigo, apellido_cliente")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("codigo");

      if (error) throw error;
      setProyectos(data || []);
      
      // Si hay proyectos y no hay uno seleccionado, seleccionar el primero por defecto
      if (data && data.length > 0 && !proyectoSeleccionado) {
        setProyectoSeleccionado(data[0].id);
      }
    } catch (error) {
      console.error("Error cargando proyectos:", error);
    }
  };

  const cargarProyectoCliente = async () => {
    try {
      console.log("🔍 Cargando proyecto:", permisosCliente.proyectoId);
      const { data, error } = await supabase
        .from("proyectos")
        .select("id, codigo, apellido_cliente")
        .eq("id", permisosCliente.proyectoId)
        .single();

      console.log("📊 Proyecto cargado:", data);
      console.log("❌ Error proyecto:", error);
      
      if (error) throw error;
      setProyectoCliente(data);
    } catch (error) {
      console.error("Error cargando proyecto:", error);
    }
  };

  const cargarDatosConstructora = async (proyectoId) => {
    console.log('🚀 cargarDatosConstructora llamado con:', {
      proyectoId,
      timestamp: new Date().toISOString()
    });
    
    if (!proyectoId) {
      console.log('⚠️ No hay proyecto seleccionado');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Cargar proyecto seleccionado
      const { data: proyectoData } = await supabase
        .from("proyectos")
        .select("id, codigo, apellido_cliente")
        .eq("id", proyectoId)
        .single();
      
      setProyectoCliente(proyectoData);

      // Cargar saldos de cajas del proyecto específico
      const { data: cajasData } = await supabase
        .from("cajas")
        .select("id, nombre, moneda, balance_actual, tipo_caja")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .eq("activo", true);

      console.log('💰 Cajas del proyecto específico:', {
        proyectoId,
        proyectoCodigo: proyectoData?.codigo,
        cajasData,
        cajasCount: cajasData?.length
      });

      // Cargar transacciones (calculará saldos desde flujo filtrado)
      await cargarTransaccionesProyecto(proyectoId);
    } catch (error) {
      console.error("Error cargando datos constructora:", error);
    } finally {
      setLoading(false);
    }
  };

  const cargarTransaccionesProyecto = async (proyectoId) => {
    try {
      // Cargar cajas del proyecto (solo tipo "proyecto", excluir "mano_obra")
      const { data: cajasProyecto } = await supabase
        .from("cajas")
        .select("id, nombre, moneda")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .eq("tipo_caja", "proyecto")
        .eq("activo", true);

      const cajasProyectoIds = (cajasProyecto || []).map(c => c.id);
      const cajasMap = {};
      cajasProyecto?.forEach(caja => {
        cajasMap[caja.id] = { nombre: caja.nombre, moneda: caja.moneda };
      });

      // Cargar conceptos
      const { data: conceptosEntrada } = await supabase
        .from("conceptos_entrada")
        .select("id, nombre")
        .eq("constructora_id", constructora.id);

      const { data: conceptosSalida } = await supabase
        .from("conceptos_salida")
        .select("id, nombre")
        .eq("constructora_id", constructora.id);

      const conceptosMap = {};
      conceptosEntrada?.forEach(c => { conceptosMap[c.id] = c.nombre; });
      conceptosSalida?.forEach(c => { conceptosMap[c.id] = c.nombre; });

      // Cargar entradas del proyecto y filtrar solo "Adelanto de caja"
      const { data: entradas } = await supabase
        .from("entradas")
        .select("id, fecha, created_at, monto, moneda, tipo_cambio, descripcion, comprobante_url, factura_url, tiene_comprobante, tiene_factura, concepto_id, caja_destino_id, caja_origen_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .in("caja_destino_id", cajasProyectoIds)
        .eq("activo", true)
        .is("deleted_at", null);

      const entradasAdelantoCaja = (entradas || []).filter(e => {
        const nombre = (conceptosMap[e.concepto_id] || "").toLowerCase();
        return nombre === "adelanto de caja" || nombre === "adelanto caja";
      });

      // Cargar salidas
      const { data: salidas } = await supabase
        .from("salidas")
        .select("id, fecha, created_at, monto, moneda, tipo_cambio, descripcion, comprobante_url, factura_url, tiene_comprobante, tiene_factura, concepto_id, caja_origen_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .in("caja_origen_id", cajasProyectoIds)
        .eq("activo", true)
        .is("deleted_at", null);

      // Cargar cambios de moneda
      const { data: cambios } = await supabase
        .from("cambios_moneda")
        .select("id, fecha, created_at, monto_origen, monto_destino, moneda_origen, moneda_destino, tipo_cambio, descripcion, caja_origen_id, caja_destino_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .or(`caja_origen_id.in.(${cajasProyectoIds.join(",")}),caja_destino_id.in.(${cajasProyectoIds.join(",")})`)
        .eq("activo", true)
        .is("deleted_at", null);

      const todasTransacciones = [
        ...(entradasAdelantoCaja || []).map(entrada => ({
          ...entrada,
          tipo: "entrada",
          concepto_nombre: conceptosMap[entrada.concepto_id] || "Entrada",
          caja_nombre: cajasMap[entrada.caja_destino_id]?.nombre || "Caja",
          caja_moneda: cajasMap[entrada.caja_destino_id]?.moneda || "ARS"
        })),
        ...(salidas || []).map(salida => ({
          ...salida,
          tipo: "salida",
          concepto_nombre: conceptosMap[salida.concepto_id] || "Gasto",
          caja_nombre: cajasMap[salida.caja_origen_id]?.nombre || "Caja",
          caja_moneda: cajasMap[salida.caja_origen_id]?.moneda || "ARS"
        })),
        ...(cambios || []).flatMap(cambio => {
          const baseTime = new Date(cambio.created_at).getTime();
          const salidaTime = new Date(baseTime - 1000);
          const entradaTime = new Date(baseTime);
          
          return [
            {
              id: `${cambio.id}_salida`,
              fecha: cambio.fecha,
              created_at: salidaTime.toISOString(),
              tipo: "cambio_salida",
              monto: cambio.monto_origen,
              moneda: cambio.moneda_origen,
              tipo_cambio: cambio.tipo_cambio,
              descripcion: `Cambio USD a ARS - ${cambio.descripcion || ""}`,
              comprobante_url: null,
              factura_url: null,
              tiene_comprobante: false,
              tiene_factura: false,
              concepto_id: null,
              caja_origen_id: cambio.caja_origen_id,
              caja_destino_id: null,
              concepto_nombre: "Cambio de moneda",
              caja_nombre: cajasMap[cambio.caja_origen_id]?.nombre || "Caja",
              caja_moneda: cambio.moneda_origen
            },
            {
              id: `${cambio.id}_entrada`,
              fecha: cambio.fecha,
              created_at: entradaTime.toISOString(),
              tipo: "cambio_entrada",
              monto: cambio.monto_destino,
              moneda: cambio.moneda_destino,
              tipo_cambio: cambio.tipo_cambio,
              descripcion: `Cambio USD a ARS - ${cambio.descripcion || ""}`,
              comprobante_url: null,
              factura_url: null,
              tiene_comprobante: false,
              tiene_factura: false,
              concepto_id: null,
              caja_origen_id: null,
              caja_destino_id: cambio.caja_destino_id,
              concepto_nombre: "Cambio de moneda",
              caja_nombre: cajasMap[cambio.caja_destino_id]?.nombre || "Caja",
              caja_moneda: cambio.moneda_destino
            }
          ];
        })
      ];

      const { transaccionesARSConSaldo, transaccionesUSDConSaldo } = calcularSaldosPorMoneda(todasTransacciones);
      
      // Combinar y ordenar: primero por FECHA, luego por CREATED_AT (MÁS RECIENTE PRIMERO)
      const transaccionesConSaldo = [...transaccionesARSConSaldo, ...transaccionesUSDConSaldo].sort((a, b) => {
        const fechaA = new Date(a.fecha).getTime();
        const fechaB = new Date(b.fecha).getTime();
        
        // Si las fechas son diferentes, ordenar por fecha
        if (fechaA !== fechaB) {
          return fechaB - fechaA; // Descendente: más reciente primero
        }
        
        // Si las fechas son iguales, desempatar por created_at
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return createdB - createdA; // Descendente: más reciente primero
      });

      // Actualizar saldos desde el flujo filtrado (último calculado = saldo final)
      const saldoARS = transaccionesARSConSaldo.length > 0 
        ? transaccionesARSConSaldo[transaccionesARSConSaldo.length - 1].saldoAcumulado 
        : 0;
      const saldoUSD = transaccionesUSDConSaldo.length > 0 
        ? transaccionesUSDConSaldo[transaccionesUSDConSaldo.length - 1].saldoAcumulado 
        : 0;
      setSaldosCajas({ ARS: saldoARS, USD: saldoUSD });

      setTransacciones(transaccionesConSaldo);
    } catch (error) {
      console.error("Error cargando transacciones proyecto:", error);
    }
  };

  const calcularSaldosPorMoneda = (transaccionesTodas) => {
    // Separar transacciones por moneda
    const transaccionesARS = transaccionesTodas.filter(t => t.moneda === "ARS");
    const transaccionesUSD = transaccionesTodas.filter(t => t.moneda === "USD");
    
    // Función interna para calcular saldos por moneda
    const calcularPorMoneda = (transacciones) => {
      // 1. Ordenar cronológicamente (más antiguo primero) para calcular saldos correctamente
      // Primero por FECHA, luego por CREATED_AT como desempate
      const ordenadas = [...transacciones].sort((a, b) => {
        const fechaA = new Date(a.fecha).getTime();
        const fechaB = new Date(b.fecha).getTime();
        
        // Si las fechas son diferentes, ordenar por fecha
        if (fechaA !== fechaB) {
          return fechaA - fechaB; // Ascendente: más antiguo primero
        }
        
        // Si las fechas son iguales, desempatar por created_at
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return createdA - createdB; // Ascendente: más antiguo primero
      });
      
      // 2. Calcular saldos acumulados hacia adelante (del más antiguo al más reciente)
      let saldoAcumulado = 0;
      const conSaldos = ordenadas.map(transaccion => {
        const monto = parseFloat(transaccion.monto || 0);
        const esEntrada = transaccion.tipo === "entrada" || transaccion.tipo === "cambio_entrada";
        const montoTransaccion = esEntrada ? monto : -monto;
        
        saldoAcumulado += montoTransaccion;
        
        return {
          ...transaccion,
          montoTransaccion: Math.round(montoTransaccion * 100) / 100,
          saldoAcumulado: Math.round(saldoAcumulado * 100) / 100
        };
      });
      
      // 3. NO ordenar aquí, lo haremos al final después de combinar ARS y USD
      return conSaldos;
    };
    
    // Calcular saldos para cada moneda por separado
    const transaccionesARSConSaldo = calcularPorMoneda(transaccionesARS);
    const transaccionesUSDConSaldo = calcularPorMoneda(transaccionesUSD);
    
    return { transaccionesARSConSaldo, transaccionesUSDConSaldo };
  };

  const cargarSaldosCajas = async () => {
    try {
      const { data, error } = await supabase
        .from("cajas")
        .select("moneda, balance_actual")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", permisosCliente.proyectoId)
        .eq("tipo_caja", "proyecto")
        .eq("activo", true);

      if (error) throw error;

      const totales = { ARS: 0, USD: 0 };
      data?.forEach((caja) => {
        totales[caja.moneda] += parseFloat(caja.balance_actual || 0);
      });
      setSaldosCajas(totales);
    } catch (error) {
      console.error("Error cargando saldos de cajas:", error);
    }
  };

  const cargarTransacciones = async () => {
    try {
      console.log("🔍 Cargando transacciones para proyecto:", permisosCliente.proyectoId);
      console.log("🏢 Constructora ID:", constructora.id);


      // Cargar cajas del proyecto para filtrar entradas
      const { data: cajasProyecto } = await supabase
        .from("cajas")
        .select("id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", permisosCliente.proyectoId)
        .eq("activo", true);

      const cajasProyectoIds = (cajasProyecto || []).map(c => c.id);

      // Cargar entradas: origen cliente, destino caja proyecto
      const { data: entradas, error: errorEntradas } = await supabase
        .from("entradas")
        .select("id, fecha, created_at, monto, moneda, tipo_cambio, descripcion, comprobante_url, factura_url, tiene_comprobante, tiene_factura, concepto_id, caja_destino_id, caja_origen_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", permisosCliente.proyectoId)
        .in("caja_destino_id", cajasProyectoIds)
        .eq("activo", true)
        .is("deleted_at", null);

      console.log("📊 Entradas encontradas:", entradas?.length || 0);
      if (errorEntradas) console.log("❌ Error entradas:", errorEntradas);

      // Reutilizar cajasProyectoIds que ya se cargaron arriba

      // Cargar salidas solo desde cajas del proyecto
      const { data: salidas, error: errorSalidas } = await supabase
        .from("salidas")
        .select("id, fecha, created_at, monto, moneda, tipo_cambio, descripcion, comprobante_url, factura_url, tiene_comprobante, tiene_factura, concepto_id, caja_origen_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", permisosCliente.proyectoId)
        .in("caja_origen_id", cajasProyectoIds)
        .eq("activo", true)
        .is("deleted_at", null);

      console.log("📊 Salidas encontradas:", salidas?.length || 0);
      if (errorSalidas) console.log("❌ Error salidas:", errorSalidas);

      // Cargar cambios de moneda
      const { data: cambios, error: errorCambios } = await supabase
        .from("cambios_moneda")
        .select("id, fecha, created_at, monto_origen, monto_destino, moneda_origen, moneda_destino, tipo_cambio, descripcion, caja_origen_id, caja_destino_id")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", permisosCliente.proyectoId)
        .in("caja_origen_id", cajasProyectoIds)
        .in("caja_destino_id", cajasProyectoIds)
        .eq("activo", true)
        .is("deleted_at", null);

      console.log("📊 Cambios encontrados:", cambios?.length || 0);
      if (errorCambios) console.log("❌ Error cambios:", errorCambios);

      // Cargar conceptos y cajas para mostrar nombres reales
      const { data: conceptos } = await supabase
        .from("conceptos_entrada")
        .select("id, nombre")
        .eq("activo", true);

      const { data: cajas } = await supabase
        .from("cajas")
        .select("id, nombre, moneda")
        .eq("activo", true);

      // Crear mapas para lookup rápido
      const conceptosMap = (conceptos || []).reduce((acc, c) => ({ ...acc, [c.id]: c.nombre }), {});
      const cajasMap = (cajas || []).reduce((acc, c) => ({ ...acc, [c.id]: c }), {});

      // Combinar y ordenar transacciones
      const todasTransacciones = [
        ...(entradas || []).map(entrada => ({
          ...entrada,
          tipo: "entrada",
          concepto_nombre: conceptosMap[entrada.concepto_id] || "Entrada",
          caja_nombre: cajasMap[entrada.caja_destino_id]?.nombre || "Caja",
          caja_moneda: cajasMap[entrada.caja_destino_id]?.moneda || "ARS"
        })),
        ...(salidas || []).map(salida => ({
          ...salida,
          tipo: "salida",
          concepto_nombre: conceptosMap[salida.concepto_id] || "Gasto",
          caja_nombre: cajasMap[salida.caja_origen_id]?.nombre || "Caja",
          caja_moneda: cajasMap[salida.caja_origen_id]?.moneda || "ARS"
        })),
        // Agregar cambios de moneda como dos transacciones separadas pero con tipo especial
        ...(cambios || []).flatMap(cambio => {
          // Crear timestamps ligeramente diferentes para mantener orden
          const baseTime = new Date(cambio.created_at).getTime();
          const salidaTime = new Date(baseTime - 1000); // 1 segundo antes
          const entradaTime = new Date(baseTime); // tiempo original
          
          return [
            // Salida de la caja origen - timestamp menor para aparecer primero
            {
              id: `${cambio.id}_salida`,
              fecha: cambio.fecha,
              created_at: salidaTime.toISOString(),
              tipo: "cambio_salida",
              monto: cambio.monto_origen,
              moneda: cambio.moneda_origen,
              tipo_cambio: cambio.tipo_cambio,
              descripcion: `Cambio USD a ARS - ${cambio.descripcion || ""}`,
              comprobante_url: null,
              factura_url: null,
              tiene_comprobante: false,
              tiene_factura: false,
              concepto_id: null,
              caja_origen_id: cambio.caja_origen_id,
              caja_destino_id: null,
              concepto_nombre: "Cambio de moneda",
              caja_nombre: cajasMap[cambio.caja_origen_id]?.nombre || "Caja",
              caja_moneda: cambio.moneda_origen
            },
            // Entrada a la caja destino - timestamp original
            {
              id: `${cambio.id}_entrada`,
              fecha: cambio.fecha,
              created_at: entradaTime.toISOString(),
              tipo: "cambio_entrada",
              monto: cambio.monto_destino,
              moneda: cambio.moneda_destino,
              tipo_cambio: cambio.tipo_cambio,
              descripcion: `Cambio USD a ARS - ${cambio.descripcion || ""}`,
              comprobante_url: null,
              factura_url: null,
              tiene_comprobante: false,
              tiene_factura: false,
              concepto_id: null,
              caja_origen_id: null,
              caja_destino_id: cambio.caja_destino_id,
              concepto_nombre: "Cambio de moneda",
              caja_nombre: cajasMap[cambio.caja_destino_id]?.nombre || "Caja",
              caja_moneda: cambio.moneda_destino
            }
          ];
        })
      ];

      console.log("📊 Total transacciones combinadas:", todasTransacciones.length);
      console.log("🔍 Transacciones antes del ordenamiento cronológico:", todasTransacciones.slice(0, 5).map(t => ({
        id: t.id,
        fecha: t.fecha,
        tipo: t.tipo,
        monto: t.monto
      })));

      // Calcular saldos para cada moneda por separado
      const { transaccionesARSConSaldo, transaccionesUSDConSaldo } = calcularSaldosPorMoneda(todasTransacciones);
      
      // Debug: verificar created_at de todas las transacciones
      console.log("🔍 Debug created_at de todas las transacciones:");
      [...transaccionesARSConSaldo, ...transaccionesUSDConSaldo].forEach(t => {
        if (t.fecha === "2025-09-30") {
          console.log("📅 Transacción del 30/09:", {
            tipo: t.tipo,
            moneda: t.moneda,
            created_at: t.created_at,
            created_at_type: typeof t.created_at,
            fecha: t.fecha,
            monto: t.monto
          });
        }
      });

      // Combinar y ordenar para mostrar usando SOLO created_at
      const transaccionesConSaldo = [...transaccionesARSConSaldo, ...transaccionesUSDConSaldo].sort((a, b) => {
        // Verificar si created_at existe
        if (!a.created_at || !b.created_at) {
          console.log("❌ created_at faltante:", { a: a.created_at, b: b.created_at });
          // Si falta created_at, usar fecha como fallback
          const fechaA = new Date(a.fecha);
          const fechaB = new Date(b.fecha);
          return fechaB.getTime() - fechaA.getTime();
        }
        
        const createdA = new Date(a.created_at);
        const createdB = new Date(b.created_at);
        
        return createdB.getTime() - createdA.getTime(); // Más reciente primero (timestamp)
      });
      
      setTransacciones(transaccionesConSaldo);
    } catch (error) {
      console.error("Error cargando transacciones:", error);
    }
  };

  const exportarCSV = () => {
    const headers = [
      "Fecha",
      "Tipo",
      "Concepto",
      "Descripción",
      "Comprobante",
      "Caja",
      "Monto",
      "Saldo Caja"
    ];

         const rows = transacciones.map(t => [
           formatDateForDisplay(t.fecha),
           t.tipo === "entrada" ? "Entrada de caja" : 
           t.tipo === "salida" ? "Gasto" : 
           t.tipo === "cambio_entrada" ? "Cambio USD a ARS" : "Cambio USD a ARS",
           t.concepto_nombre,
           t.descripcion || "",
           t.tiene_comprobante ? "Comprobante" : (t.tiene_factura ? "Factura" : "No"),
           `${t.caja_nombre} (${t.caja_moneda})`,
           formatearMonto(t.montoTransaccion),
           t.saldoAcumulado === 0 ? "0" : formatearMonto(t.saldoAcumulado)
         ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `flujo_caja_${proyectoCliente?.nombre || "proyecto"}_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ===== Exportar PDF (desde último Adelanto de caja) =====
  const exportarPDF = async () => {
    try {
      if (isConstructora && !proyectoSeleccionado) return;
      // Usar el dataset ya renderizado en la tabla (mismas filas)
      const normalizar = (s = "") => s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

      // Buscar la última "Entrada de caja" con concepto exactamente "Adelanto de caja"
      // Buscar el último índice del adelanto dentro del orden actual (descendente por fecha)
      let idxAdelanto = -1;
      for (let i = transacciones.length - 1; i >= 0; i--) {
        const t = transacciones[i];
        if (t.tipo === "entrada" && normalizar(t.concepto_nombre || "") === normalizar("Adelanto de caja")) {
          idxAdelanto = i;
          break;
        }
      }

      // Si existe, incluir desde ese registro HASTA los más recientes (índices 0..idxAdelanto)
      const filas = idxAdelanto >= 0 ? transacciones.slice(0, idxAdelanto + 1) : transacciones;

      // Preparar datos para PDF
      const saldoActualARS = saldosCajas.ARS || 0;
      const saldoActualUSD = saldosCajas.USD || 0;

      // Lazy import jsPDF/autotable para no impactar carga
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF("p", "pt");
      const proyectoCodigo = isConstructora ? (proyectos.find(p => p.id === proyectoSeleccionado)?.codigo || "-") : (proyectoCliente?.codigo || "-");

      // Obtener apellido del cliente para el proyecto
      const proyectoNombre = isConstructora 
        ? (proyectos.find(p => p.id === proyectoSeleccionado)?.apellido_cliente || proyectoCodigo)
        : (proyectoCliente?.apellido_cliente || proyectoCodigo);

      // Título principal con estilo mejorado
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(15, 23, 42); // Azul muy oscuro
      doc.text(`Flujo de caja`, 40, 50);
      
      // Línea decorativa debajo del título
      doc.setDrawColor(59, 130, 246); // Azul
      doc.setLineWidth(3);
      doc.line(40, 55, 200, 55);
      
      // Información del proyecto
      doc.setFontSize(16);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(71, 85, 105); // Gris medio
      doc.text(`Proyecto: ${proyectoNombre}`, 40, 75);
      
      // Información de generación (solo fecha, sin hora)
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128); // Gris claro
      doc.text(`Generado: ${new Date().toLocaleDateString()}`, 40, 90);
      
      // Saldos actuales en formato de cajas con bordes redondados (derecha del encabezado)
      const saldoY = 40; // Misma altura que el título principal
      const saldoBoxWidth = 200;
      const saldoBoxHeight = 50;
      const saldoX = 350; // Posición a la derecha
      
      // Fondo de la caja con bordes redondados
      doc.setFillColor(248, 250, 252); // Fondo gris muy claro
      doc.roundedRect(saldoX, saldoY, saldoBoxWidth, saldoBoxHeight, 8, 8, 'F');
      
      // Borde de la caja
      doc.setDrawColor(226, 232, 240); // Borde gris claro
      doc.setLineWidth(1);
      doc.roundedRect(saldoX, saldoY, saldoBoxWidth, saldoBoxHeight, 8, 8, 'S');
      
      // Título de la caja
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(51, 65, 85); // Texto gris oscuro
      doc.text(`Saldo actual`, saldoX + 10, saldoY + 18);
      
      // Valores de saldo
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(71, 85, 105); // Texto gris medio
      doc.text(`ARS: $${Number(saldoActualARS).toLocaleString("es-AR")}`, saldoX + 10, saldoY + 32);
      doc.text(`USD: US$${Number(saldoActualUSD).toLocaleString("es-AR")}`, saldoX + 10, saldoY + 45);

      // Tabla
      const head = [["Fecha", "Tipo", "Concepto", "Descripción", "Caja", "Monto", "TC", "Saldo"]];
      const body = filas.map(t => [
        formatDateForDisplay(t.fecha),
        t.tipo.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()), // Capitalizar primera letra de cada palabra
        t.concepto_nombre || "-",
        t.descripcion || "-",
        `${t.caja_nombre} (${t.caja_moneda})`,
        `${t.moneda === "USD" ? "US$" : "$"}${(Math.abs(t.montoTransaccion ?? t.monto ?? 0)).toLocaleString("es-AR")}`,
        String(t.tipo_cambio || 1),
        `${t.moneda === "USD" ? "US$" : "$"}${(t.saldoAcumulado || 0).toLocaleString("es-AR")}`,
      ]);

      autoTable(doc, {
        startY: 110,
        head,
        body,
        styles: { fontSize: 9, cellPadding: 6 },
        headStyles: { fillColor: [59, 130, 246] },
        theme: "striped",
        didParseCell: (data) => {
          if (data.section === 'body') {
            const row = filas[data.row.index];
            const esEntrada = (row.montoTransaccion ?? row.monto ?? 0) >= 0 || row.tipo === 'entrada' || row.tipo === 'cambio_entrada';
            const saldo = row.saldoAcumulado || 0;
            
            // Suave fondo por tipo
            data.cell.styles.fillColor = esEntrada ? [240, 253, 244] : [254, 242, 242];
            
            // Colorear "Monto"
            const colMonto = 5;
            if (data.column.index === colMonto) {
              data.cell.styles.textColor = esEntrada ? [16, 185, 129] : [239, 68, 68];
              data.cell.styles.fontStyle = 'bold';
            }
            
            // Colorear "Saldo" según positivo/negativo
            const colSaldo = 7;
            if (data.column.index === colSaldo) {
              data.cell.styles.textColor = saldo >= 0 ? [16, 185, 129] : [239, 68, 68]; // Verde si positivo, rojo si negativo
              data.cell.styles.fontStyle = 'bold';
            }
            
            // Colorear "Tipo"
            if (data.column.index === 1) {
              data.cell.styles.textColor = esEntrada ? [16, 185, 129] : [239, 68, 68];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });

      doc.save(`flujo_caja_${proyectoCodigo}_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (e) {
      console.error("Error exportando PDF:", e);
      alert("No se pudo generar el PDF");
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "400px",
        flexDirection: "column",
        gap: "16px"
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "4px solid #f3f4f6",
          borderTop: "4px solid #06b6d4",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }} />
        <p style={{ color: "#6b7280", fontSize: "16px" }}>Cargando flujo de caja...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? "16px" : "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div
        className="header-section"
        style={{
          marginBottom: "16px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            minHeight: "56px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#2563eb",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Wallet size={20} color="#ffffff" />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                }}
              >
                {isConstructora ? "Flujos de caja" : "Flujo de Caja"}
              </h1>
              <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
                {proyectoCliente?.codigo || (isConstructora ? "Seleccioná un proyecto" : "Proyecto")}
              </p>
            </div>
          </div>

          {/* Contenedor para filtro y botones de exportación */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Filtro de proyecto (solo para constructora) */}
            {isConstructora && (
              <div style={{ 
                minWidth: isMobile ? "160px" : "220px",
                maxWidth: isMobile ? "200px" : "280px"
              }}>
                {/* Debug info */}
                {console.log('🎯 Debug dropdown:', {
                  proyectoSeleccionado,
                  proyectosDisponibles: proyectos.map(p => ({ id: p.id, codigo: p.codigo })),
                  opciones: proyectos.map((proyecto) => ({
                    value: proyecto.id,
                    label: proyecto.codigo,
                  })),
                  opcionSeleccionada: proyectos.map((proyecto) => ({
                    value: proyecto.id,
                    label: proyecto.codigo,
                  })).find(opt => opt.value === proyectoSeleccionado),
                  timestamp: new Date().toISOString()
                })}
                <CustomDropdown
                  value={proyectoSeleccionado}
                  onChange={(value) => {
                    console.log('🔄 Cambio de proyecto en dropdown:', value);
                    setProyectoSeleccionado(value);
                  }}
                  options={proyectos.map((proyecto) => ({
                    value: proyecto.id,
                    label: proyecto.codigo,
                  }))}
                  placeholder="Seleccionar proyecto"
                  icon={Building2}
                />
              </div>
            )}

            {/* Botón exportar CSV */}
            <button
              onClick={exportarCSV}
              disabled={isConstructora && !proyectoSeleccionado}
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "4px" : "8px",
                padding: isMobile ? "8px 12px" : "10px 16px",
                background: (isConstructora && !proyectoSeleccionado) ? "#9ca3af" : "#1f2937",
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "500",
                cursor: (isConstructora && !proyectoSeleccionado) ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              onMouseOver={(e) => {
                if (!isConstructora || proyectoSeleccionado) {
                  e.target.style.background = "#374151";
                  e.target.style.transform = "translateY(-1px)";
                }
              }}
              onMouseOut={(e) => {
                if (!isConstructora || proyectoSeleccionado) {
                  e.target.style.background = "#1f2937";
                  e.target.style.transform = "translateY(0)";
                }
              }}
            >
              <Download size={isMobile ? 14 : 16} /> 
              {isMobile ? "CSV" : "Exportar CSV"}
            </button>

            {/* Botón exportar PDF (solo constructoras) */}
            {isConstructora && (
              <button
                onClick={exportarPDF}
                disabled={!proyectoSeleccionado}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? "4px" : "8px",
                  padding: isMobile ? "8px 12px" : "10px 16px",
                  background: !proyectoSeleccionado ? "#9ca3af" : "#2563eb",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "500",
                  cursor: !proyectoSeleccionado ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                onMouseOver={(e) => {
                  if (proyectoSeleccionado) {
                    e.target.style.background = "#1d4ed8";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseOut={(e) => {
                  if (proyectoSeleccionado) {
                    e.target.style.background = "#2563eb";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                <Download size={isMobile ? 14 : 16} />
                {isMobile ? "PDF" : "Descargar PDF"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Saldos actuales como tarjetas sueltas (fuera del header) */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", 
        gap: isMobile ? "12px" : "16px",
        marginBottom: isMobile ? "16px" : "20px"
      }}>
        <div style={{
          background: "#f8fafc",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          border: "1px solid #e2e8f0"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <DollarSign size={isMobile ? 14 : 16} style={{ color: "#64748b" }} />
            <span style={{ fontSize: isMobile ? "12px" : "14px", color: "#64748b", fontWeight: "500" }}>Saldo en ARS</span>
          </div>
          <div style={{ fontSize: isMobile ? "20px" : "24px", fontWeight: "700", color: "#1f2937" }}>
            ${saldosCajas.ARS === 0 ? "0" : formatearMonto(saldosCajas.ARS)}
          </div>
        </div>
        <div style={{
          background: "#f8fafc",
          borderRadius: "12px",
          padding: isMobile ? "12px" : "16px",
          border: "1px solid #e2e8f0"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <DollarSign size={isMobile ? 14 : 16} style={{ color: "#64748b" }} />
            <span style={{ fontSize: isMobile ? "12px" : "14px", color: "#64748b", fontWeight: "500" }}>Saldo en USD</span>
          </div>
          <div style={{ fontSize: isMobile ? "20px" : "24px", fontWeight: "700", color: "#1f2937" }}>
            ${saldosCajas.USD === 0 ? "0" : formatearMonto(saldosCajas.USD)}
          </div>
        </div>
      </div>

      {/* Tabla de transacciones */}
      <div style={{
        background: "white",
        borderRadius: isMobile ? "12px" : "16px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        overflow: "hidden"
      }}>
        {transacciones.length === 0 ? (
          <div style={{
            padding: "48px 24px",
            textAlign: "center",
            color: "#6b7280"
          }}>
            <Wallet size={48} style={{ margin: "0 auto 16px", opacity: 0.5 }} />
            <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>No hay transacciones</h3>
            <p style={{ margin: 0, fontSize: "14px" }}>
              {isConstructora && (!proyectoSeleccionado || proyectoSeleccionado === "todos") 
                ? "Aún no se han registrado transacciones de caja."
                : "Aún no se han registrado entradas o salidas de caja para este proyecto."
              }
            </p>
          </div>
        ) : isMobile ? (
          // MÓVIL: Cards
          <div style={{ padding: "8px" }}>
            {transacciones.map((transaccion, index) => (
              <div
                key={`${transaccion.tipo}-${transaccion.id}`}
                style={{
                  background: "white",
                  border: "1px solid #f1f5f9",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                {/* Header */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px"
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#1e293b",
                      marginBottom: "4px"
                    }}>
                      {formatDateForDisplay(transaccion.fecha)}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: "500"
                    }}>
                      {transaccion.concepto_nombre}
                    </div>
                  </div>
                  <div style={{
                    textAlign: "right",
                    marginLeft: "12px"
                  }}>
                    <div style={{
                      fontSize: "16px",
                      fontWeight: "800",
                      color: transaccion.montoTransaccion >= 0 ? "#10b981" : "#ef4444",
                      marginBottom: "2px"
                    }}>
                      {transaccion.montoTransaccion >= 0 ? "+" : ""}{transaccion.moneda === "USD" ? "US$" : "$"}
                      {formatearMonto(Math.abs(transaccion.montoTransaccion))}
                    </div>
                  </div>
                </div>

                {/* Tipo */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px"
                }}>
                  {transaccion.tipo === "entrada" ? (
                    <>
                      <TrendingUp size={14} style={{ color: "#10b981" }} />
                      <span style={{ color: "#10b981", fontSize: "12px", fontWeight: "500" }}>Entrada de caja</span>
                    </>
                  ) : transaccion.tipo === "salida" ? (
                    <>
                      <TrendingDown size={14} style={{ color: "#ef4444" }} />
                      <span style={{ color: "#ef4444", fontSize: "12px", fontWeight: "500" }}>Gasto</span>
                    </>
                  ) : transaccion.tipo === "cambio_entrada" ? (
                    <>
                      <DollarSign size={14} style={{ color: "#f59e0b" }} />
                      <span style={{ color: "#f59e0b", fontSize: "12px", fontWeight: "500" }}>Cambio USD a ARS</span>
                    </>
                  ) : (
                    <>
                      <DollarSign size={14} style={{ color: "#f59e0b" }} />
                      <span style={{ color: "#f59e0b", fontSize: "12px", fontWeight: "500" }}>Cambio USD a ARS</span>
                    </>
                  )}
                </div>

                {/* Descripción si existe */}
                {transaccion.descripcion && (
                  <div style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginBottom: "8px"
                  }}>
                    {transaccion.descripcion}
                  </div>
                )}

                {/* Documentos */}
                {(transaccion.tiene_comprobante || transaccion.tiene_factura) && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "8px",
                    flexWrap: "wrap"
                  }}>
                    {transaccion.tiene_comprobante && transaccion.comprobante_url && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = transaccion.comprobante_url;
                          link.target = '_blank';
                          link.rel = 'noopener noreferrer';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        style={{
                          fontSize: "10px",
                          background: "#dcfce7",
                          color: "#166534",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <FileText size={10} />
                        Comprobante
                      </button>
                    )}
                    {transaccion.tiene_factura && transaccion.factura_url && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = transaccion.factura_url;
                          link.target = '_blank';
                          link.rel = 'noopener noreferrer';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        style={{
                          fontSize: "10px",
                          background: "#fef3cd",
                          color: "#92400e",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                      >
                        <FileText size={10} />
                        Factura
                      </button>
                    )}
                  </div>
                )}

                {/* Footer con caja y saldo */}
                <div style={{
                  borderTop: "1px solid #f1f5f9",
                  paddingTop: "8px",
                  marginTop: "8px"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "11px",
                    color: "#64748b",
                    marginBottom: "6px"
                  }}>
                    <span>{transaccion.caja_nombre} ({transaccion.caja_moneda})</span>
                  </div>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px",
                    background: "#f8fafc",
                    borderRadius: "8px"
                  }}>
                    <span style={{
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: "500"
                    }}>
                      Saldo después:
                    </span>
                    <span style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#1e293b"
                    }}>
                      {transaccion.moneda === "USD" ? "US$" : "$"}
                      {transaccion.saldoAcumulado === 0 ? "0" : formatearMonto(transaccion.saldoAcumulado)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse",
              tableLayout: "fixed"
            }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "left", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "15%" : "10%"
                  }}>
                    Fecha
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "left", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "18%" : "12%"
                  }}>
                    Tipo
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "left", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "20%" : "15%"
                  }}>
                    Concepto
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "left", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "0%" : "18%"
                  }}>
                    {isMobile ? "" : "Descripción"}
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "left", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "12%" : "12%"
                  }}>
                    {isMobile ? "Doc." : "Comp./Fact."}
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "left", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "0%" : "10%"
                  }}>
                    {isMobile ? "" : "Caja"}
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "right", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "23%" : "11%"
                  }}>
                    Monto
                  </th>
                  <th style={{ 
                    padding: isMobile ? "12px 8px" : "16px", 
                    textAlign: "right", 
                    fontWeight: "600", 
                    color: "#374151", 
                    fontSize: isMobile ? "12px" : "14px",
                    width: isMobile ? "0%" : "12%"
                  }}>
                    {isMobile ? "" : "Saldo Caja"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {transacciones.map((transaccion, index) => (
                  <tr
                    key={`${transaccion.tipo}-${transaccion.id}`}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background-color 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    <td style={{ padding: isMobile ? "12px 8px" : "16px", fontSize: isMobile ? "12px" : "14px", color: "#374151" }}>
                      {formatDateForDisplay(transaccion.fecha)}
                    </td>
                    <td style={{ padding: isMobile ? "12px 8px" : "16px" }}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: "500"
                      }}>
                        {transaccion.tipo === "entrada" ? (
                          <>
                            <TrendingUp size={14} style={{ color: "#10b981" }} />
                            <span style={{ color: "#10b981" }}>Entrada de caja</span>
                          </>
                        ) : transaccion.tipo === "salida" ? (
                          <>
                            <TrendingDown size={14} style={{ color: "#ef4444" }} />
                            <span style={{ color: "#ef4444" }}>Gasto</span>
                          </>
                        ) : transaccion.tipo === "cambio_entrada" ? (
                          <>
                            <ArrowRightLeft size={14} style={{ color: "#f59e0b" }} />
                            <span style={{ color: "#f59e0b" }}>Cambio USD a ARS</span>
                          </>
                        ) : (
                          <>
                            <ArrowRightLeft size={14} style={{ color: "#f59e0b" }} />
                            <span style={{ color: "#f59e0b" }}>Cambio USD a ARS</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: isMobile ? "12px 8px" : "16px", fontSize: isMobile ? "12px" : "14px", color: "#374151" }}>
                      {transaccion.concepto_nombre}
                    </td>
                    {!isMobile && (
                      <td style={{ padding: "16px", fontSize: "14px", color: "#374151" }}>
                        {transaccion.descripcion || "-"}
                      </td>
                    )}
       <td style={{ padding: isMobile ? "12px 8px" : "16px", fontSize: isMobile ? "12px" : "14px", color: "#374151" }}>
         <div style={{
           display: "flex",
           alignItems: "center",
           gap: isMobile ? "4px" : "8px",
           flexWrap: "wrap",
         }}>
           {transaccion.tiene_comprobante && (
             <button
               onClick={() => window.open(transaccion.comprobante_url, "_blank")}
               style={{
                 fontSize: isMobile ? "9px" : "10px",
                 background: "#dcfce7",
                 color: "#166534",
                 padding: isMobile ? "2px 4px" : "4px 8px",
                 borderRadius: "6px",
                 border: "none",
                 cursor: "pointer",
                 transition: "all 0.2s ease",
                 fontWeight: "500",
                 display: "flex",
                 alignItems: "center",
                 gap: isMobile ? "2px" : "4px",
                 touchAction: "manipulation",
               }}
               onMouseEnter={(e) => {
                 e.target.style.background = "#bbf7d0";
                 e.target.style.transform = "translateY(-1px)";
               }}
               onMouseLeave={(e) => {
                 e.target.style.background = "#dcfce7";
                 e.target.style.transform = "translateY(0)";
               }}
             >
               <FileText size={isMobile ? 8 : 10} />
               {isMobile ? "Comp." : "Comprobante"}
             </button>
           )}
           {transaccion.tiene_factura && (
             <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
               <button
                 onClick={() => window.open(transaccion.factura_url, "_blank")}
                 style={{
                 fontSize: isMobile ? "9px" : "10px",
                 background: "#fef3cd",
                 color: "#92400e",
                 padding: isMobile ? "2px 4px" : "4px 8px",
                 borderRadius: "6px",
                 border: "none",
                 cursor: "pointer",
                 transition: "all 0.2s ease",
                 fontWeight: "500",
                 display: "flex",
                 alignItems: "center",
                 gap: isMobile ? "2px" : "4px",
                 touchAction: "manipulation",
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.background = "#fed7aa";
                   e.target.style.transform = "translateY(-1px)";
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.background = "#fef3cd";
                   e.target.style.transform = "translateY(0)";
                 }}
               >
               <FileText size={isMobile ? 8 : 10} />
               {isMobile ? "Fact." : "Factura"}
               </button>
               {transaccion.tipo_factura && (
                 <span style={{
                   background: transaccion.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                   color: transaccion.tipo_factura === 'A' ? '#166534' : '#92400e',
                   padding: '2px 6px',
                   borderRadius: '4px',
                   fontSize: '10px',
                   fontWeight: '600',
                   flexShrink: 0
                 }}>
                   {transaccion.tipo_factura}
                 </span>
               )}
             </div>
           )}
           {!transaccion.tiene_comprobante && !transaccion.tiene_factura && (
             <span style={{ color: "#9ca3af" }}>No</span>
           )}
         </div>
       </td>
                    {!isMobile && (
                      <td style={{ padding: "16px", fontSize: "14px", color: "#374151" }}>
                        {transaccion.caja_nombre} ({transaccion.caja_moneda})
                      </td>
                    )}
                    <td style={{ padding: isMobile ? "12px 8px" : "16px", fontSize: isMobile ? "12px" : "14px", textAlign: "right" }}>
                      <span style={{
                        color: transaccion.montoTransaccion >= 0 ? "#10b981" : "#ef4444",
                        fontWeight: "500"
                      }}>
                        {transaccion.montoTransaccion >= 0 ? "+" : ""}{transaccion.moneda === "USD" ? "US$" : "$"}{formatearMonto(transaccion.montoTransaccion)}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={{ padding: "16px", fontSize: "14px", textAlign: "right", fontWeight: "600" }}>
                        {transaccion.moneda === "USD" ? "US$" : "$"}{transaccion.saldoAcumulado === 0 ? "0" : formatearMonto(transaccion.saldoAcumulado)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CSS para animación de loading */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default CajaCliente;

